import {
  type GetFavoritesArgs,
  type GetTopAssetsArgs,
  useAddToFavoritesMutation,
  useGetCurrentUserQuery,
  useLazyGetDemoQuery,
  useLazyGetPlaybookQuery,
  useLazyGetTourQuery,
  useRemoveFromFavoritesMutation,
} from '@carboncopyco/data';
import {
  MoreVerticalOutlineIcon,
  PlayCircleIcon,
  RightOutlineIcon,
  ShareOutlineIcon,
  ShowOutlineIcon,
  StarIcon,
  StarOutlineIcon,
  UnstarIcon,
} from '@carboncopyco/icons';
import type { Role, TopAsset } from '@carboncopyco/types';
import {
  Button,
  ColorHex,
  Dropdown,
  DropdownItem,
  SubMenu,
  TextCaption,
  Tooltip,
  Typography,
  notify,
} from '@carboncopyco/ui';
import { PlayEventNames, isDesktop, useAnalytics } from '@carboncopyco/utils';
import NiceModal from '@ebay/nice-modal-react';
import { useBoolean } from 'ahooks';
import classNames from 'classnames';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { type ReactElement, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { DemoLinkModal, DemoLinkPlaybookModal } from '../DemoLinkModal';
import { GiveADemoFlowModal } from '../PresentationModals';
import { playParentEntityToIdKey } from '../PresentationModals/GiveADemoFlowModal/consts';
import { ShareDemoFlow } from '../Share';
import { CreateShareTourFlow } from '../Share/ShareTourFlow';
import { useFilesServiceContext } from '../utils';
import { useConsoleState } from './ConsoleContext';
import { useAssetStatus } from './useAssetStatus';

type TopAssetsTableRowWrapperProps = {
  asset: TopAsset;
  children: ReactElement;
  getTopAssetsArgs: GetTopAssetsArgs;
  getFavoritesArgs: GetFavoritesArgs;
};

export const TopAssetsTableRowWrapper = ({
  asset,
  children,
  getTopAssetsArgs,
  getFavoritesArgs,
}: TopAssetsTableRowWrapperProps) => {
  const { assetId, assetType, workspaceId, isFavorite } = asset;
  const { trackEvent } = useAnalytics();
  const eventPayload = {
    [playParentEntityToIdKey[assetType]]: assetId,
    location: 'Console.give_a_demo_modal',
  };
  const trackPlayNow = () =>
    trackEvent(PlayEventNames.PLAY_CLICK_PLAY_NOW, eventPayload);
  const trackPlayLater = () => {
    trackEvent(PlayEventNames.PLAY_CLICK_PLAY_LATER, eventPayload);
  };

  const { filesServiceDomain } = useFilesServiceContext();
  const flags = useFlags();
  const [showCreatePlaybookPlayModal, setShowCreatePlaybookPlayModal] =
    useBoolean();
  const [showCreateDemoPlayModal, setShowCreateDemoPlayModal] = useBoolean();
  const [showDemoLinkModal, setShowDemoLinkModal] = useBoolean();
  const [isDropdownOpen, setIsDropdownOpen] = useBoolean();
  const { organizationId } = useParams();
  const [addToFavorites] = useAddToFavoritesMutation();
  const [removeFromFavorites] = useRemoveFromFavoritesMutation();
  const {
    previewDemo,
    previewTour,
    previewPlaybook,
    handlePlay,
    goToAssetPage,
    openGiveADemoModal,
    openGiveAPlaybookModal,
  } = useConsoleState();
  const [getDemo, { data: demo, isLoading: isDemoLoading }] =
    useLazyGetDemoQuery();
  const [getPlaybook, { data: playbook, isLoading: isPlaybookLoading }] =
    useLazyGetPlaybookQuery();
  const [getTour] = useLazyGetTourQuery();
  const previewRef = useRef<HTMLDivElement>();
  const shareInternallyRef = useRef<HTMLDivElement>();
  const shareExternallyRef = useRef<HTMLDivElement>();
  const shareMenuRef = useRef<HTMLDivElement>();
  const { isDisabled } = useAssetStatus({ asset });
  const [isStarHovered, setIsStarHovered] = useState(false);
  const [allowHoverToSetStarIcon, setAllowHoverToSetStarIcon] = useState(false);
  const { data: userData } = useGetCurrentUserQuery();

  const userRole: Role = userData?.user?.roles?.[organizationId];

  const isPlaybook = assetType === 'playbook';
  const isDemo = assetType === 'demo';
  const isTour = assetType === 'tour';
  const isDisabledShareInternally = asset.status === 'shared' || isTour;
  const isDisabledShareExternally =
    asset.recordingType === 'live_demo' || isPlaybook;
  return (
    <div className="group">
      <div
        className={classNames(
          'px-6 group-hover:bg-ds-natural-50 flex justify-between h-14',
          {
            'bg-ds-natural-150': isDropdownOpen,
          }
        )}
      >
        <div className="flex items-center">{children}</div>
        <div className="flex items-center gap-0.5">
          <div className="w-10">
            {(isDemo || isPlaybook) && !isDisabled && (
              <div
                className={classNames(
                  'group-hover:visible',
                  isDropdownOpen ? 'visible' : 'invisible'
                )}
              >
                <Button
                  icon={<PlayCircleIcon color={ColorHex.primary} />}
                  secondary
                  link
                  disabled={isDemoLoading || isPlaybookLoading}
                  onClick={
                    !isDemoLoading && !isPlaybookLoading
                      ? () => {
                          switch (assetType) {
                            case 'demo':
                              getDemo({
                                organizationId,
                                workspaceId,
                                id: assetId,
                              })
                                .unwrap()
                                .then((demo) => {
                                  openGiveADemoModal
                                    ? openGiveADemoModal(demo)
                                    : setShowCreateDemoPlayModal.setTrue();
                                });

                              break;
                            case 'playbook':
                              getPlaybook({
                                organizationId,
                                workspaceId,
                                id: assetId,
                              })
                                .unwrap()
                                .then((playbook) => {
                                  openGiveAPlaybookModal
                                    ? openGiveAPlaybookModal(playbook)
                                    : setShowCreatePlaybookPlayModal.setTrue();
                                });
                          }
                        }
                      : null
                  }
                />
              </div>
            )}
          </div>
          {isFavorite ? (
            <Button
              onMouseOver={() => {
                if (allowHoverToSetStarIcon) {
                  setIsStarHovered(true);
                }
              }}
              onMouseOut={() => {
                setAllowHoverToSetStarIcon(true);
                setIsStarHovered(false);
              }}
              secondary
              link
              icon={isStarHovered ? <UnstarIcon /> : <StarIcon />}
              onClick={() => {
                removeFromFavorites({
                  organizationId,
                  workspaceId: asset.workspaceId,
                  assetType,
                  assetId,
                  getTopAssetsArgs,
                  getFavoritesArgs,
                });
              }}
            />
          ) : (
            <div
              className={classNames(
                'shrink-0',
                isDisabled ? 'invisible' : 'group-hover:visible',
                isDropdownOpen ? 'visible' : 'invisible'
              )}
            >
              <Button
                secondary
                link
                icon={<StarOutlineIcon />}
                onClick={() => {
                  setAllowHoverToSetStarIcon(false);
                  addToFavorites({
                    organizationId,
                    workspaceId: asset.workspaceId,
                    assetType,
                    assetId,
                    getTopAssetsArgs,
                    getFavoritesArgs,
                  });
                }}
              />
            </div>
          )}
          <div
            className={classNames(
              'shrink-0',
              !isDisabled || isDropdownOpen ? 'visible' : 'invisible'
            )}
          >
            <Dropdown
              open={isDropdownOpen}
              onOpenChange={setIsDropdownOpen.toggle}
              width={200}
              asChild
              triggerElement={
                <Button
                  secondary
                  link
                  icon={<MoreVerticalOutlineIcon />}
                />
              }
            >
              <div ref={previewRef}>
                <DropdownItem
                  disabled={isPlaybook && !isDesktop()}
                  iconLeft={<ShowOutlineIcon />}
                  label="Preview"
                  onClick={() => {
                    switch (assetType) {
                      case 'demo':
                        getDemo({
                          organizationId,
                          workspaceId,
                          id: assetId,
                        })
                          .unwrap()
                          .then((demo) => {
                            previewDemo(demo, '');
                          });
                        break;
                      case 'playbook':
                        previewPlaybook(assetId, workspaceId);
                        break;
                      case 'tour':
                        previewTour(assetId, workspaceId);
                    }
                  }}
                />
              </div>
              {isPlaybook && !isDesktop() && (
                <Tooltip
                  anchorRef={previewRef}
                  placement="top-end"
                  noArrow
                >
                  <div className="p-3 w-56">
                    <TextCaption label="Previewing Playbooks on web is not supported at the moment. Please use the " />
                    <div
                      className="cursor-pointer inline-block text-ds-200 font-ds-inter text-ds-primary-300"
                      onClick={() => {
                        previewPlaybook(assetId, workspaceId);
                      }}
                    >
                      desktop app
                    </div>
                    <TextCaption label="." />
                  </div>
                </Tooltip>
              )}
              {!(isDisabledShareInternally && isDisabledShareExternally) && (
                <div ref={shareMenuRef}>
                  <SubMenu
                    width={205}
                    triggerProps={{
                      label: 'Share',
                      iconRight: <RightOutlineIcon />,
                      iconLeft: <ShareOutlineIcon size={18} />,
                    }}
                  >
                    <div ref={shareInternallyRef}>
                      <DropdownItem
                        label="Share internally"
                        onClick={setShowDemoLinkModal.setTrue}
                        disabled={isDisabledShareInternally}
                      />
                    </div>
                    {isTour && (
                      <Tooltip
                        noArrow
                        placement="top-end"
                        anchorRef={shareInternallyRef}
                      >
                        <div className="p-3">
                          <Typography
                            fontSize="text-ds-200"
                            lineHeight="leading-ds-200"
                            color="text-ds-natural"
                            fontWeight="font-ds-normal"
                            letterSpacing="tracking-ds-tight"
                            label="Tours can only be shared externally"
                          />
                        </div>
                      </Tooltip>
                    )}

                    <div ref={shareExternallyRef}>
                      <DropdownItem
                        disabled={isDisabledShareExternally}
                        label="Share externally"
                        onClick={() => {
                          switch (assetType) {
                            case 'demo':
                              NiceModal.show(ShareDemoFlow, {
                                workspaceId,
                                demoId: Number(assetId),
                                goToShares: () => {
                                  getDemo({
                                    organizationId,
                                    workspaceId,
                                    id: assetId,
                                  })
                                    .unwrap()
                                    .then(() => {
                                      goToAssetPage(
                                        {
                                          workspaceId,
                                          id: assetId,
                                          type: assetType,
                                        },
                                        { tab: 'links' }
                                      );
                                    });
                                },
                              });
                              break;

                            case 'tour':
                              NiceModal.show(CreateShareTourFlow, {
                                workspaceId,
                                toursShareAspectRatioEnabled:
                                  flags.toursShareAspectRatio,
                                organizationId,
                                tourId: Number(assetId),
                                goToShares: () => {
                                  getTour({
                                    organizationId,
                                    workspaceId,
                                    id: Number(assetId),
                                  })
                                    .unwrap()
                                    .then(() => {
                                      goToAssetPage(
                                        {
                                          workspaceId,
                                          id: assetId,
                                          type: assetType,
                                        },
                                        { tab: 'links' }
                                      );
                                    });
                                },
                              });
                              break;
                          }
                        }}
                      />
                    </div>
                    {isPlaybook && (
                      <Tooltip
                        noArrow
                        placement="right"
                        anchorRef={shareExternallyRef}
                      >
                        <div className="p-3">
                          <Typography
                            fontSize="text-ds-200"
                            lineHeight="leading-ds-200"
                            color="text-ds-natural"
                            fontWeight="font-ds-normal"
                            letterSpacing="tracking-ds-tight"
                            label="Playbooks can only be shared internally"
                          />
                        </div>
                      </Tooltip>
                    )}
                  </SubMenu>
                </div>
              )}
            </Dropdown>
          </div>
        </div>

        {showDemoLinkModal && isDemo && (
          <DemoLinkModal
            workspaceId={workspaceId}
            assetId={assetId}
            assetType="demo"
            organizationId={organizationId}
            isOpen={showDemoLinkModal}
            onClose={setShowDemoLinkModal.setFalse}
            role={userRole}
          />
        )}
        {showDemoLinkModal && isPlaybook && (
          <DemoLinkPlaybookModal
            workspaceId={workspaceId}
            assetId={assetId}
            assetType="playbook"
            organizationId={organizationId}
            isOpen={showDemoLinkModal}
            onClose={setShowDemoLinkModal.setFalse}
            role={userRole}
          />
        )}
      </div>

      {!openGiveADemoModal && showCreatePlaybookPlayModal && playbook && (
        <GiveADemoFlowModal
          workspaceId={workspaceId}
          open={showCreatePlaybookPlayModal}
          asset={{
            name: playbook.name,
            id: playbook.id,
            type: 'playbook',
            assets: playbook.assets,
          }}
          onClose={setShowCreatePlaybookPlayModal.setFalse}
          onPresent={(play) => {
            trackPlayNow();
            handlePlay(play);
          }}
          onSave={() => {
            trackPlayLater();
            notify.success('Playbook presentation saved successfully');
          }}
          filesServiceUrl={filesServiceDomain}
        />
      )}

      {!openGiveADemoModal && showCreateDemoPlayModal && demo && (
        <GiveADemoFlowModal
          workspaceId={workspaceId}
          asset={{
            name: demo.demoName,
            id: String(demo.id),
            type: 'demo',
            assets: [
              {
                assetId: String(demo.id),
                assetRevisionId: demo.revisionId,
                assetType: 'demo',
                entity: demo,
                autoSync: false,
              },
            ],
          }}
          onSave={() => {
            trackPlayLater();
            notify.success('Demo presentation saved successfully');
          }}
          onClose={setShowCreateDemoPlayModal.setFalse}
          open={showCreateDemoPlayModal}
          onPresent={(play) => {
            trackPlayNow();
            handlePlay(play);
          }}
          filesServiceUrl={filesServiceDomain}
        />
      )}
    </div>
  );
};
